/**
 * useDraftGeneration Hook
 *
 * Manages the simplified "Select for Draft" workflow:
 * 1. Updates RSS Content status to "Good to go"
 * 2. Triggers n8n webhook with record ID
 * 3. n8n queries Supabase, processes the record, creates Blog Post with "Pending Approval"
 * 4. Frontend shows "Processing..." and polls until Blog Post appears
 */

import { useState, useCallback, useRef } from "react";
import { updateRSSContentStatus, fetchBlogPosts } from "api/supabase";
import { triggerDraftGeneration } from "api/webhook";

function useDraftGeneration(onSuccess) {
  const [processingItems, setProcessingItems] = useState(new Set());
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [processingStartTimes, setProcessingStartTimes] = useState(new Map());
  const pollingIntervals = useRef(new Map());

  /**
   * Get processing duration for an item
   * @param {string} rssContentId - RSS Content record ID
   * @returns {number} Duration in seconds
   */
  const getProcessingDuration = useCallback(
    (rssContentId) => {
      const startTime = processingStartTimes.get(rssContentId);
      if (!startTime) return 0;
      return Math.floor((Date.now() - startTime) / 1000);
    },
    [processingStartTimes]
  );

  /**
   * Check if Blog Post was created for the RSS Content record
   * @param {string} rssContentId - RSS Content record ID
   * @returns {Promise<boolean>} True if Blog Post exists
   */
  const checkBlogPostExists = useCallback(async (rssContentId) => {
    try {
      // Fetch Blog Posts with "Pending Approval" status
      const blogPosts = await fetchBlogPosts("Pending Approval");

      console.log("Checking for blog post. RSS Content ID:", rssContentId);
      console.log("Found blog posts:", blogPosts.length);

      // ✅ SIMPLIFIED: Single field check using the linked record field
      // This assumes the n8n workflow properly creates the "RSS Content" link when creating Blog Posts
      const blogPost = blogPosts.find((post) => post.rssContentId === rssContentId);

      if (blogPost) {
        console.log(
          "✅ Found matching blog post:",
          blogPost.id,
          "linked to RSS Content:",
          rssContentId
        );
        return true;
      }

      console.log("❌ No blog post found linked to RSS Content:", rssContentId);
      return false;
    } catch (error) {
      console.error("Error checking for Blog Post:", error);
      return false;
    }
  }, []);

  /**
   * Start polling for Blog Post creation
   * @param {string} rssContentId - RSS Content record ID
   */
  const startPolling = useCallback(
    (rssContentId) => {
      console.log("Starting polling for Blog Post creation:", rssContentId);

      const pollInterval = setInterval(async () => {
        const blogPostExists = await checkBlogPostExists(rssContentId);

        if (blogPostExists) {
          console.log("Blog Post created successfully for:", rssContentId);

          // Stop polling
          clearInterval(pollInterval);
          pollingIntervals.current.delete(rssContentId);

          // Remove from processing state
          setProcessingItems((prev) => {
            const newSet = new Set(prev);
            newSet.delete(rssContentId);
            return newSet;
          });
          setProcessingStartTimes((prev) => {
            const newMap = new Map(prev);
            newMap.delete(rssContentId);
            return newMap;
          });

          setSuccess(true);

          // Trigger success callback if provided
          if (onSuccess) {
            onSuccess(rssContentId);
          }
        }
      }, 10000); // Poll every 10 seconds (increased from 5 seconds to reduce server load)

      // Store the interval
      pollingIntervals.current.set(rssContentId, pollInterval);

      // Auto-timeout after 5 minutes
      setTimeout(() => {
        if (pollingIntervals.current.has(rssContentId)) {
          console.log("Polling timeout for:", rssContentId);
          clearInterval(pollInterval);
          pollingIntervals.current.delete(rssContentId);

          setProcessingItems((prev) => {
            const newSet = new Set(prev);
            newSet.delete(rssContentId);
            return newSet;
          });
          setProcessingStartTimes((prev) => {
            const newMap = new Map(prev);
            newMap.delete(rssContentId);
            return newMap;
          });

          setError(
            `Processing timeout for item ${rssContentId}. Please check the Drafts tab manually or try again.`
          );
        }
      }, 600000); // 10 minutes timeout (increased from 5 minutes)
    },
    [checkBlogPostExists]
  );

  /**
   * Select RSS content item for draft generation
   *
   * Simplified workflow:
   * 1. Update RSS Content status to "Good to go"
   * 2. Trigger n8n webhook with record ID
   * 3. Start polling for Blog Post creation
   * 4. Show "Processing..." until Blog Post appears
   *
   * @param {import('../types').RSSItem} rssItem - RSS content item
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  const selectForDraft = useCallback(
    async (rssItem) => {
      setError(null);
      setSuccess(false);

      try {
        console.log("Starting draft generation for RSS item:", rssItem.id);

        // Add to processing state immediately
        setProcessingItems((prev) => new Set([...prev, rssItem.id]));
        setProcessingStartTimes((prev) => new Map([...prev, [rssItem.id, Date.now()]]));

        // Step 1: Update RSS Content status to "Processing"
        console.log("Updating RSS Content status to 'Processing'...");
        await updateRSSContentStatus(rssItem.id, "Processing");

        // Step 2: Trigger n8n webhook with record ID (fire-and-forget approach)
        console.log("Triggering n8n workflow for draft generation...");

        // Start polling immediately without waiting for webhook response
        // This allows the n8n workflow to take as long as needed
        startPolling(rssItem.id);

        // Trigger webhook asynchronously - don't wait for completion
        triggerDraftGeneration(rssItem.id)
          .then((result) => {
            if (result.success) {
              console.log("Draft generation webhook triggered successfully");
            } else {
              console.error("Webhook failed:", result.error);
              // If webhook fails immediately, we should stop polling and revert status
              stopPolling(rssItem.id);
              updateRSSContentStatus(rssItem.id, "New").catch(console.error);
              setProcessingItems((prev) => {
                const newSet = new Set(prev);
                newSet.delete(rssItem.id);
                return newSet;
              });
              setError(result.error || "Failed to trigger draft generation workflow");
            }
          })
          .catch((err) => {
            console.error("Webhook error:", err);
            // Only show error if it's not a timeout (since we're using fire-and-forget)
            if (!err.message.includes("timed out")) {
              stopPolling(rssItem.id);
              updateRSSContentStatus(rssItem.id, "New").catch(console.error);
              setProcessingItems((prev) => {
                const newSet = new Set(prev);
                newSet.delete(rssItem.id);
                return newSet;
              });
              setError(err.message);
            }
          });

        return {
          success: true,
          message: "Processing... The draft will appear in the Drafts tab when ready.",
        };
      } catch (err) {
        console.error("Error in draft generation workflow:", err);
        setError(err.message);

        // Remove from processing state and try to revert status on error
        setProcessingItems((prev) => {
          const newSet = new Set(prev);
          newSet.delete(rssItem.id);
          return newSet;
        });

        try {
          await updateRSSContentStatus(rssItem.id, "New");
        } catch (revertError) {
          console.error("Failed to revert status:", revertError);
        }

        return { success: false, error: err.message };
      }
    },
    [startPolling]
  );

  /**
   * Check if a specific item is currently being processed
   * @param {string} rssContentId - RSS Content record ID
   * @returns {boolean} True if item is processing
   */
  const isProcessing = useCallback(
    (rssContentId) => {
      return processingItems.has(rssContentId);
    },
    [processingItems]
  );

  /**
   * Stop polling for a specific item (cleanup)
   * @param {string} rssContentId - RSS Content record ID
   */
  const stopPolling = useCallback((rssContentId) => {
    if (pollingIntervals.current.has(rssContentId)) {
      clearInterval(pollingIntervals.current.get(rssContentId));
      pollingIntervals.current.delete(rssContentId);

      setProcessingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(rssContentId);
        return newSet;
      });
    }
  }, []);

  const resetState = useCallback(() => {
    setError(null);
    setSuccess(false);
  }, []);

  // Cleanup on unmount
  const cleanup = useCallback(() => {
    pollingIntervals.current.forEach((interval) => {
      clearInterval(interval);
    });
    pollingIntervals.current.clear();
    setProcessingItems(new Set());
  }, []);

  return {
    selectForDraft,
    isProcessing,
    stopPolling,
    processingItems,
    getProcessingDuration,
    error,
    success,
    resetState,
    cleanup,
  };
}

export default useDraftGeneration;
